import { ColumnDef } from "@tanstack/react-table";
import type { ComponentType } from "react";
import { AssetDisplay } from "@/components/ui/asset-display";
import type { RecordWithAssets } from "@/schemas";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, ArrowUpDown, Edit, Trash2, ArrowUpRight, ArrowDownRight, RefreshCcw, DollarSign, MinusCircle, PlusCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface RecordColumnsProps {
  onDeleteRecord: (recordId: string) => void;
  onEditRecord: (record: RecordWithAssets) => void;
}

const typeStyles: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: ComponentType<{ className?: string }>;
    className: string;
  }
> = {
  buy: {
    label: "Buy",
    variant: "default",
    icon: PlusCircle,
    className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20",
  },
  sell: {
    label: "Sell",
    variant: "destructive",
    icon: MinusCircle,
    className: "bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20",
  },
  exchange: {
    label: "Exchange",
    variant: "secondary",
    icon: RefreshCcw,
    className: "bg-blue-500/10 text-blue-500 border-blue-500/20 hover:bg-blue-500/20",
  },
  currency_conversion: {
    label: "FX",
    variant: "outline",
    icon: RefreshCcw,
    className: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20 hover:bg-indigo-500/20",
  },
  deposit: {
    label: "Deposit",
    variant: "outline",
    icon: ArrowDownRight,
    className: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20 hover:bg-cyan-500/20",
  },
  withdrawal: {
    label: "Withdrawal",
    variant: "outline",
    icon: ArrowUpRight,
    className: "bg-orange-500/10 text-orange-500 border-orange-500/20 hover:bg-orange-500/20",
  },
  dividend: {
    label: "Dividend",
    variant: "outline",
    icon: DollarSign,
    className: "bg-purple-500/10 text-purple-500 border-purple-500/20 hover:bg-purple-500/20",
  },
};


export const getColumns = ({ onDeleteRecord, onEditRecord }: RecordColumnsProps): ColumnDef<RecordWithAssets>[] => [
  {
    accessorKey: "record_date",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 p-0 hover:bg-transparent"
        >
          Date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue("record_date"));
      return (
        <div className="flex flex-col">
          <span className="font-medium">
            {date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => {
      const type = row.getValue("type") as string;
      const style = typeStyles[type] || {
        label: type?.charAt(0).toUpperCase() + type?.slice(1) || "Unknown",
        variant: "outline",
        icon: MoreHorizontal,
        className: "",
      };

      const Icon = style.icon;

      return (
        <Badge 
          variant="outline" 
          className={cn("gap-1.5 font-semibold py-0.5 px-2", style.className)}
        >
          <Icon className="h-3 w-3" />
          {style.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: "buyAsset",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 p-0 hover:bg-transparent"
        >
          Details
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const record = row.original;
      
      if (record.type === 'buy' || record.type === 'sell' || record.type === 'deposit' || record.type === 'dividend' || record.type === 'withdrawal') {
        // For sell, the primary asset is the one being sold. For others, it's either buyAsset or just 'asset'
        const displayAsset = record.type === 'sell' 
          ? (record.sellAsset || record.asset) 
          : (record.buyAsset || record.asset);
          
        return displayAsset ? (
          <AssetDisplay asset={displayAsset} />
        ) : (
          <span className="text-muted-foreground italic text-xs">
            {(record.type === 'sell' ? record.asset_id_sell : record.asset_id_buy) || record.asset_id || 'Unknown'}
          </span>
        );
      }

      if (record.type === 'exchange' || record.type === 'currency_conversion') {
        return (
          <div className="flex items-center gap-2">
            {record.sellAsset ? <AssetDisplay asset={record.sellAsset} hideName /> : <span className="text-xs">{record.asset_id_sell}</span>}
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            {record.buyAsset ? <AssetDisplay asset={record.buyAsset} hideName /> : <span className="text-xs">{record.asset_id_buy}</span>}
          </div>
        );
      }

      return <span className="text-muted-foreground">-</span>;
    },
  },
  {
    accessorKey: "qty_buy",
    header: ({ column }) => {
      return (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 p-0 justify-end hover:bg-transparent"
          >
            Amount
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const record = row.original;
      const qtyBuy = record.qty_buy || record.amount || 0;
      const qtySell = record.qty_sell || record.amount || 0;
      
      if (record.type === 'buy') {
        const unitPrice = record.qty_buy ? (record.qty_sell || 0) / record.qty_buy : 0;
        const currencySymbol = record.sellAsset?.symbol || record.asset_id_sell || "USD";
        return (
          <div className="text-right space-y-0.5">
            <div className="font-bold text-emerald-500">
              +{qtyBuy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
            </div>
            <div className="text-[10px] text-muted-foreground font-medium">
              {formatCurrency(record.qty_sell || 0, currencySymbol)}
            </div>
            <div className="text-[10px] text-muted-foreground/70 italic">
              @{formatCurrency(unitPrice, currencySymbol)}
            </div>
          </div>
        );
      }

      if (record.type === 'sell') {
        const unitPrice = record.qty_sell ? (record.qty_buy || 0) / record.qty_sell : 0;
        const currencySymbol = record.buyAsset?.symbol || record.asset_id_buy || "USD";
        return (
          <div className="text-right space-y-0.5">
            <div className="font-bold text-red-500">
              -{qtySell.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
            </div>
            <div className="text-[10px] text-muted-foreground font-medium">
              {formatCurrency(record.qty_buy || 0, currencySymbol)}
            </div>
            <div className="text-[10px] text-muted-foreground/70 italic">
              @{formatCurrency(unitPrice, currencySymbol)}
            </div>
          </div>
        );
      }

      if (record.type === 'deposit' || record.type === 'dividend') {
        return (
          <div className="text-right font-bold text-emerald-500">
            +{qtyBuy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
          </div>
        );
      }

      if (record.type === 'withdrawal') {
        return (
          <div className="text-right font-bold text-red-500">
            -{qtySell.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
          </div>
        );
      }

      if (record.type === 'exchange' || record.type === 'currency_conversion') {
        const rate = qtyBuy > 0 ? qtySell / qtyBuy : 0;
        return (
          <div className="text-right space-y-0.5">
            <div className="text-[10px] text-red-400 font-medium">-{qtySell.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
            <div className="text-xs text-emerald-400 font-bold">+{qtyBuy.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
            <div className="text-[9px] text-muted-foreground/60 italic">Rate: {rate.toFixed(4)}</div>
          </div>
        );
      }

      return <div className="text-right">-</div>;
    },
  },
  {
    accessorKey: "fees",
    header: "Fees",
    cell: ({ row }) => {
      const fees = row.original.fees;
      const feeAssets = row.original.feeAssets;
      
      if (!fees || fees.length === 0) {
        return <span className="text-muted-foreground text-xs">-</span>;
      }
      return (
        <div className="flex flex-col items-start gap-1">
          {fees.map((fee, index) => {
            const asset = feeAssets?.[fee.asset_id];
            const currencyCode = asset?.symbol || fee.asset_id;
            
            return (
              <Badge key={index} variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">
                {formatCurrency(fee.amount, currencyCode)}
              </Badge>
            );
          })}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => {
      const record = row.original;

      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Record Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onEditRecord(record)}
                className="cursor-pointer"
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Record
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDeleteRecord(record.record_id)}
                className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Record
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];
